import { useDetectGPU } from '@react-three/drei'
import { useFrame } from '@react-three/fiber'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  BufferGeometry,
  InstancedMesh,
  MathUtils,
  FrontSide,
  Group,
  InstancedBufferAttribute,
  OrthographicCamera,
  PlaneGeometry,
  ShaderMaterial,
  Sphere,
  Texture,
  TextureLoader,
  UniformsLib,
  Vector3,
} from 'three'

import { useLevelGeometry, useOutlineExclusion, type TerrainMask } from '@hooks'
import { grassFrag, grassVert } from '@shaders'
import { seededRandom, useGroundControls } from '@utils'

export const GRASS_MAX_COUNT = 100000
const CHUNKS_X = 4
const CHUNKS_Z = 4

// Zoom LOD: below LOD_ZOOM_FULL (CSS px per world unit) blades thin out in
// LOD_TIERS steps down to LOD_MIN_DENSITY at LOD_ZOOM_MIN.
const LOD_TIERS = 8
const LOD_ZOOM_FULL = 80
const LOD_ZOOM_MIN = 25
const LOD_MIN_DENSITY = 0.4

const grassTexture = new TextureLoader().load(
  '/textures/grass-blades.png',
  texture => (texture.flipY = false),
)

interface GrassProps {
  maxCount?: number
  mask: TerrainMask
  groundTexture: Texture
  full?: boolean
}

interface GrassChunk {
  count: number
  /** tierEnds[t] = instances drawn when LOD tiers 0..t are enabled */
  tierEnds: number[]
  geo: BufferGeometry
  sphere: Sphere
}

export function Grass({
  maxCount = GRASS_MAX_COUNT,
  mask,
  groundTexture,
  full = true,
}: GrassProps) {
  const { shadow, grassLod } = useGroundControls()

  const { size } = useLevelGeometry()
  const frontZ = full ? undefined : size.height / 2
  const { tier } = useDetectGPU()

  const { material, chunks } = useMemo(() => {
    const { side, centerOffset } = mask
    const _count = tier < 2 ? maxCount * 0.5 : maxCount
    const _cols = Math.floor(Math.sqrt(_count))
    const _rows = Math.floor(Math.sqrt(_count))

    const baseGeo = new PlaneGeometry(1, 1)
    baseGeo.translate(0, 0.5, 0)
    baseGeo.deleteAttribute('normal')

    const mat = new ShaderMaterial({
      vertexShader: grassVert,
      fragmentShader: grassFrag,
      uniforms: {
        ...UniformsLib.lights,
        uCenterOffset: { value: centerOffset },
        uGroundTex: { value: groundTexture },
        uSide: { value: side },
        uTime: { value: 0 },
        uGrassTex: { value: grassTexture },
        uWindSpeed: { value: 0.2 },
        uWindDirection: { value: [1.0, 0.3] },
        uWindDivergence: { value: 0.4 },
        uWindStrength: { value: 0.6 },
        uReceiveShadow: { value: 1.0 },
      },
      side: FrontSide,
      depthTest: true,
      depthWrite: true,
      transparent: false,
      lights: true,
    })

    const rf = Math.min(1, mask.radiusFactor)
    const isCentered = frontZ === undefined
    const zMinNorm = isCentered ? -0.5 * rf : -frontZ! / side
    const zRange = isCentered ? 1.0 * rf : (0.5 - zMinNorm) * rf
    const stepX = (side * rf) / _cols
    const stepZ = (side * zRange) / _rows
    const BIAS = 2
    const MAX_R = Math.SQRT2 / 2
    const JITTER_AMP = 0.9

    const chunkW = side / CHUNKS_X
    const chunkD = side / CHUNKS_Z
    // per chunk: flat [x, z, rotation, heightScale, lodRank] tuples
    const buckets: number[][] = Array.from({ length: CHUNKS_X * CHUNKS_Z }, () => [])

    for (let row = 0; row < _rows; row++) {
      for (let col = 0; col < _cols; col++) {
        const nx = (col / _cols - 0.5) * rf
        const nzOffset = isCentered ? row / _rows - 0.5 : (row / _rows) * zRange
        const r = Math.sqrt(nx * nx + nzOffset * nzOffset)
        let scale = 0
        if (r > 0) {
          const rBiased = Math.pow(r / MAX_R, BIAS) * MAX_R
          scale = rBiased / r
        }
        const jx = (seededRandom() - 0.5) * stepX * JITTER_AMP
        const jz = (seededRandom() - 0.5) * stepZ * JITTER_AMP
        const zBase = isCentered ? 0 : zMinNorm * side
        const x = nx * scale * side + centerOffset + jx
        const z = zBase + nzOffset * scale * side + jz
        const rot = (seededRandom() - 0.5) * Math.PI + Math.PI / 4
        const jitter = seededRandom()

        // Cull on CPU what the shader used to collapse per vertex every frame
        const { green, density } = mask.sampleGrass(x, z)
        if (jitter >= green) continue

        const xi = Math.max(
          0,
          Math.min(CHUNKS_X - 1, Math.floor((x - centerOffset + side / 2) / chunkW)),
        )
        const zi = Math.max(0, Math.min(CHUNKS_Z - 1, Math.floor((z + side / 2) / chunkD)))
        // jitter / green is uniform in [0, 1) for survivors: reuse it as LOD rank
        const lodRank = jitter / green
        buckets[zi * CHUNKS_X + xi].push(x, z, rot, 0.4 + 0.6 * density, lodRank)
      }
    }

    const chunks: GrassChunk[] = buckets
      .map((bucket, bi) => {
        const xi = bi % CHUNKS_X
        const zi = Math.floor(bi / CHUNKS_X)
        const cx = xi * chunkW - side / 2 + centerOffset
        const cz = zi * chunkD - side / 2
        const count = bucket.length / 5
        const lodTier = (j: number) => Math.floor(bucket[j * 5 + 4] * LOD_TIERS)
        const depth = (j: number) => bucket[j * 5] - bucket[j * 5 + 1]

        // Grouped by LOD tier so a draw-count prefix thins blades uniformly; within
        // a tier front-to-back for the default camera (azimuth -45°: camera sits at
        // -x, +z) so early depth rejection skips occluded blades despite the alpha test.
        const order = Array.from({ length: count }, (_, j) => j).sort(
          (a, b) => lodTier(a) - lodTier(b) || depth(a) - depth(b),
        )
        const aData = new Float32Array(count * 4)
        const tierEnds = new Array<number>(LOD_TIERS).fill(0)
        order.forEach((src, dst) => {
          aData.set(bucket.slice(src * 5, src * 5 + 4), dst * 4)
          tierEnds[lodTier(src)]++
        })
        for (let t = 1; t < LOD_TIERS; t++) tierEnds[t] += tierEnds[t - 1]

        const geo = baseGeo.clone()
        geo.setAttribute('aInstanceData', new InstancedBufferAttribute(aData, 4))

        const sphere = new Sphere(
          new Vector3(cx + chunkW / 2, 0, cz + chunkD / 2),
          Math.sqrt((chunkW / 2) ** 2 + (chunkD / 2) ** 2),
        )

        return { count, tierEnds, geo, sphere }
      })
      .filter(c => c.count > 0)

    baseGeo.dispose()
    return { material: mat, chunks }
  }, [mask, tier, maxCount, frontZ, groundTexture])

  useEffect(() => {
    return () => {
      material.dispose()
      chunks.forEach(chunk => chunk.geo.dispose())
    }
  }, [material, chunks])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/immutability
    material.uniforms.uReceiveShadow.value = shadow ? 1.0 : 0.0
  }, [material, shadow])

  const [group, setGroup] = useState<Group | null>(null)
  useOutlineExclusion(group)

  const lodTierRef = useRef(-1)
  useEffect(() => {
    lodTierRef.current = -1
  }, [chunks])

  useFrame(({ clock }) => {
    // eslint-disable-next-line react-hooks/immutability
    material.uniforms.uTime.value = clock.getElapsedTime()
  })

  useFrame(({ camera }) => {
    let lodTier = LOD_TIERS - 1
    if (grassLod && (camera as OrthographicCamera).isOrthographicCamera) {
      const t = MathUtils.smoothstep(camera.zoom, LOD_ZOOM_MIN, LOD_ZOOM_FULL)
      const density = MathUtils.lerp(LOD_MIN_DENSITY, 1, t)
      lodTier = Math.min(LOD_TIERS - 1, Math.ceil(density * LOD_TIERS) - 1)
    }
    if (!group || lodTier === lodTierRef.current) return
    lodTierRef.current = lodTier
    group.children.forEach((mesh, i) => {
      ;(mesh as InstancedMesh).count = chunks[i].tierEnds[lodTier]
    })
  })

  return (
    <group ref={setGroup}>
      {chunks.map((chunk, i) => (
        <instancedMesh
          receiveShadow
          key={i}
          args={[chunk.geo, material, chunk.count]}
          ref={mesh => {
            if (mesh) mesh.boundingSphere = chunk.sphere
          }}
        />
      ))}
    </group>
  )
}
